/**
 * ULD 复称清单 OCR 解析页面
 */

import React, { useState, useEffect, useRef } from "react";
import { Upload, Table, Button, Tag, message, Drawer, Spin, Row, Col, Divider, InputNumber, Input, DatePicker, Tooltip } from "antd";
import { InboxOutlined, EyeOutlined, DownloadOutlined, ReloadOutlined, SyncOutlined, CheckCircleOutlined, CloseCircleOutlined, LeftOutlined, RightOutlined, SaveOutlined } from "@ant-design/icons";
import uldOcrApi from "../../../api/uldOcrApi";
import dayjs from "dayjs";
import "./UldOcrPage.css";

const { Dragger } = Upload;

const UldOcrPage = () => {
    const [historyList, setHistoryList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [editedData, setEditedData] = useState([]);
    const [editedFlightNumber, setEditedFlightNumber] = useState("");
    const [editedFlightDate, setEditedFlightDate] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const pollingTimerRef = useRef(null);

    // 加载历史记录
    const loadHistory = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const response = await uldOcrApi.getHistory({ page, page_size: pageSize });
            setHistoryList(response.data.items || []);
            setPagination({
                current: page,
                pageSize,
                total: response.data.total || 0,
            });
        } catch (error) {
            message.error("加载历史记录失败");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // 启动轮询
    const startPolling = () => {
        // 清除旧的轮询
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
        }
        
        // 每3秒刷新一次
        pollingTimerRef.current = setInterval(() => {
            checkAndRefresh();
        }, 3000);
    };

    // 停止轮询
    const stopPolling = () => {
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
    };

    // 检查并刷新（如果有识别中的记录）
    const checkAndRefresh = async () => {
        try {
            // 使用 silent 模式，避免网络波动时显示错误提示
            const response = await uldOcrApi.getHistory(pagination.current, pagination.pageSize, { silent: true });
            if (response.data && response.data.items) {
                const hasProcessing = response.data.items.some((item) => item.status === "PROCESSING");
                
                // 更新列表
                setHistoryList(response.data.items);
                setPagination((prev) => ({ ...prev, total: response.data.total }));
                
                // 如果没有识别中的记录，停止轮询
                if (!hasProcessing) {
                    stopPolling();
                }
            }
        } catch (error) {
            // 轮询出错不显示错误提示，避免干扰用户
            console.error("轮询刷新失败:", error);
        }
    };

    // 初始加载
    useEffect(() => {
        loadHistory();
        
        // 组件卸载时清除轮询
        return () => {
            stopPolling();
        };
    }, []);

    // 上传配置
    const uploadProps = {
        name: "files",
        multiple: true,
        accept: ".pdf,.jpg,.jpeg,.png,.bmp,.tiff,.tif,.webp",
        showUploadList: false,
        maxCount: 20,
        beforeUpload: (file, fileList) => {
            // beforeUpload 对每个文件都会触发一次，只在第一个文件时处理
            if (file !== fileList[0]) {
                return false;
            }
            
            // 校验文件类型
            const validExtensions = /\.(pdf|jpg|jpeg|png|bmp|tiff|tif|webp)$/i;
            const invalidFiles = fileList.filter(f => !validExtensions.test(f.name));
            
            if (invalidFiles.length > 0) {
                message.error(`不支持的文件格式: ${invalidFiles.map(f => f.name).join(", ")}`);
                return false;
            }

            // 校验文件大小 (50MB)
            const oversizedFiles = fileList.filter(f => f.size > 50 * 1024 * 1024);
            if (oversizedFiles.length > 0) {
                message.error(`文件过大（超过50MB）: ${oversizedFiles.map(f => f.name).join(", ")}`);
                return false;
            }

            // 一次性上传所有文件
            handleUpload(fileList);
            return false; // 阻止自动上传
        },
    };

    // 处理上传
    const handleUpload = async (files) => {
        // 支持单文件或多文件
        const fileArray = Array.isArray(files) ? files : [files];
        const fileCount = fileArray.length;
        
        // 统计文件类型
        const pdfCount = fileArray.filter(f => f.name.toLowerCase().endsWith('.pdf')).length;
        const imageCount = fileCount - pdfCount;
        
        let taskDesc = '';
        if (pdfCount > 0 && imageCount > 0) {
            taskDesc = `${pdfCount} 个 PDF + ${imageCount} 张图片`;
        } else if (pdfCount > 0) {
            taskDesc = `${pdfCount} 个 PDF`;
        } else {
            taskDesc = `${imageCount} 张图片`;
        }
        
        const hideLoading = message.loading(`正在解析 ${taskDesc}...`, 0);
        setUploading(true);
        try {
            const response = await uldOcrApi.parse(fileArray);

            hideLoading();
            if (response.data.success) {
                message.success(response.data.message || "解析成功，结果已添加到列表", 3);
                // 刷新列表
                await loadHistory(1, pagination.pageSize);
                // 启动轮询（如果有识别中的记录）
                startPolling();
            } else {
                message.error(response.data.message || "解析失败");
            }
        } catch (error) {
            hideLoading();
            message.error("解析失败，请重试");
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    // 预览识别结果
    const handlePreview = async (record) => {
        if (record.status !== "SUCCESS") {
            message.warning("仅成功的记录可以预览");
            return;
        }

        setPreviewVisible(true);
        setPreviewLoading(true);
        setCurrentFileIndex(0);
        setHasChanges(false);

        try {
            const response = await uldOcrApi.getHistoryDetail(record.id);
            setPreviewData(response.data);
            // 初始化可编辑数据
            setEditedData(response.data.result_data || []);
            setEditedFlightNumber(response.data.flight_number || "");
            setEditedFlightDate(response.data.flight_date ? dayjs(response.data.flight_date) : null);
        } catch (error) {
            message.error("获取详情失败");
            console.error(error);
        } finally {
            setPreviewLoading(false);
        }
    };

    // 处理单元格编辑
    const handleCellChange = (index, field, value) => {
        const newData = [...editedData];
        newData[index] = {
            ...newData[index],
            [field]: value
        };
        setEditedData(newData);
        setHasChanges(true);
    };

    const handleSaveChanges = async () => {
        if (!previewData?.id || saving) return;
        setSaving(true);
        try {
            const response = await uldOcrApi.updateHistory(previewData.id, {
                flight_number: editedFlightNumber.trim() ? editedFlightNumber.trim() : null,
                flight_date: editedFlightDate ? editedFlightDate.format("YYYY-MM-DD") : null,
                result_data: editedData,
            });
            setPreviewData(response.data);
            setEditedData(response.data.result_data || []);
            setEditedFlightNumber(response.data.flight_number || "");
            setEditedFlightDate(response.data.flight_date ? dayjs(response.data.flight_date) : null);
            setHasChanges(false);
            message.success("已保存，下载的 Excel 会使用这份修改");
            loadHistory(pagination.current, pagination.pageSize);
        } catch (error) {
            const detail = error.response?.data?.detail || error.response?.data?.message;
            message.error(detail || "保存失败");
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    // 下载 Excel
    const handleDownloadExcel = async (record) => {
        if (record.status !== "SUCCESS") {
            message.warning("仅成功的记录可以下载");
            return;
        }

        try {
            const response = await uldOcrApi.getHistoryDetail(record.id);
            const signedUrl = response.data.excel_signed_url;

            if (signedUrl) {
                window.open(signedUrl, "_blank");
            } else {
                message.error("下载链接获取失败");
            }
        } catch (error) {
            message.error("下载失败");
            console.error(error);
        }
    };

    // 下载原始 PDF
    const handleDownloadPdf = async (record) => {
        try {
            const response = await uldOcrApi.getHistoryDetail(record.id);
            const signedUrl = response.data.pdf_signed_url;

            if (signedUrl) {
                window.open(signedUrl, "_blank");
            } else {
                message.error("下载链接获取失败");
            }
        } catch (error) {
            message.error("下载失败");
            console.error(error);
        }
    };

    // 表格列定义
    const columns = [
        {
            title: "文件名",
            dataIndex: "file_name",
            key: "file_name",
            ellipsis: true,
            render: (text, record) => (
                <a
                    onClick={() => handleDownloadPdf(record)}
                    style={{ color: "#1890ff", cursor: "pointer" }}
                >
                    {text}
                </a>
            ),
        },
        {
            title: "航班号",
            dataIndex: "flight_number",
            key: "flight_number",
            width: 120,
            render: (text) => text || "-",
        },
        {
            title: "日期",
            dataIndex: "flight_date",
            key: "flight_date",
            width: 120,
            render: (text) => (text ? dayjs(text).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status, record) => {
                const statusMap = {
                    PROCESSING: { text: "识别中", color: "blue", icon: <SyncOutlined spin /> },
                    SUCCESS: { text: "成功", color: "green", icon: <CheckCircleOutlined /> },
                    FAILED: { text: "失败", color: "red", icon: <CloseCircleOutlined /> },
                };

                const config = statusMap[status] || { text: status, color: "default" };
                const tag = (
                    <Tag color={config.color} icon={config.icon}>
                        {config.text}
                    </Tag>
                );
                if (status === "FAILED" && record.error_msg) {
                    return <Tooltip title={record.error_msg}>{tag}</Tooltip>;
                }
                return tag;
            },
        },
        {
            title: "上传时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 180,
            render: (text) => dayjs(text).format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            title: "操作",
            key: "action",
            width: 200,
            fixed: "right",
            render: (_, record) => (
                <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        disabled={record.status !== "SUCCESS"}
                        onClick={() => handlePreview(record)}
                    >
                        预览
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<DownloadOutlined />}
                        disabled={record.status !== "SUCCESS"}
                        onClick={() => handleDownloadExcel(record)}
                    >
                        下载
                    </Button>
                </div>
            ),
        },
    ];

    // 预览数据表格列（可编辑）
    const previewColumns = [
        { 
            title: "板斗号", 
            dataIndex: "uld_number", 
            key: "uld_number", 
            width: 150,
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => handleCellChange(index, "uld_number", e.target.value)}
                    style={{ width: "100%" }}
                />
            )
        },
        { 
            title: "板型", 
            dataIndex: "contour_code", 
            key: "contour_code", 
            width: 100,
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => handleCellChange(index, "contour_code", e.target.value)}
                    style={{ width: "100%" }}
                />
            )
        },
        { 
            title: "容器重量(kg)", 
            dataIndex: "tare_weight", 
            key: "tare_weight", 
            width: 130,
            render: (value, record, index) => (
                <InputNumber
                    value={value}
                    onChange={(val) => handleCellChange(index, "tare_weight", val)}
                    style={{ width: "100%" }}
                    stringMode
                />
            )
        },
        { 
            title: "垫板重量(kg)", 
            dataIndex: "plate_weight", 
            key: "plate_weight", 
            width: 130,
            render: (value, record, index) => (
                <InputNumber
                    value={value}
                    onChange={(val) => handleCellChange(index, "plate_weight", val)}
                    style={{ width: "100%" }}
                    stringMode
                />
            )
        },
        { 
            title: "货物重量(kg)", 
            dataIndex: "net_weight", 
            key: "net_weight", 
            width: 130,
            render: (value, record, index) => (
                <InputNumber
                    value={value}
                    onChange={(val) => handleCellChange(index, "net_weight", val)}
                    style={{ width: "100%" }}
                    stringMode
                />
            )
        },
        { 
            title: "组货毛重(kg)", 
            dataIndex: "gross_weight", 
            key: "gross_weight", 
            width: 130,
            render: (value, record, index) => (
                <InputNumber
                    value={value}
                    onChange={(val) => handleCellChange(index, "gross_weight", val)}
                    style={{ width: "100%" }}
                    stringMode
                />
            )
        },
        { 
            title: "复磅毛重(kg)", 
            dataIndex: "re_weight", 
            key: "re_weight", 
            width: 130,
            render: (value, record, index) => (
                <InputNumber
                    value={value}
                    onChange={(val) => handleCellChange(index, "re_weight", val)}
                    style={{ width: "100%" }}
                    stringMode
                />
            )
        },
        { 
            title: "误差", 
            dataIndex: "deviation", 
            key: "deviation", 
            width: 100,
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => handleCellChange(index, "deviation", e.target.value)}
                    style={{ width: "100%" }}
                />
            )
        },
    ];

    // 表格分页变化
    const handleTableChange = (pagination) => {
        loadHistory(pagination.current, pagination.pageSize);
    };

    return (
        <div className="uld-ocr-page">
            <div className="uld-ocr-header">
                <h2>ULD 复称清单解析</h2>
                <Button icon={<ReloadOutlined />} onClick={() => loadHistory(pagination.current, pagination.pageSize)}>
                    刷新
                </Button>
            </div>

            {/* 上传区域 */}
            <div className="uld-ocr-upload-section">
                <Spin spinning={uploading} tip="正在解析，请稍候...">
                    <Dragger {...uploadProps} disabled={uploading}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">点击或拖拽文件到此处上传（支持多个文件）</p>
                        <p className="ant-upload-hint">
                            支持 PDF、JPG、PNG、BMP、TIFF、WebP 格式<br/>
                            多张图片将合并为一条记录，多个PDF将分别生成记录<br/>
                            单个文件不超过 50MB，最多 20 个文件
                        </p>
                    </Dragger>
                </Spin>
            </div>

            {/* 历史记录表格 */}
            <div className="uld-ocr-table-section">
                <Table
                    columns={columns}
                    dataSource={historyList}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1000 }}
                />
            </div>

            {/* 预览抽屉 */}
            <Drawer
                title="识别结果预览"
                placement="right"
                open={previewVisible}
                onClose={() => {
                    if (hasChanges) {
                        if (window.confirm("有未保存的修改，确定要关闭吗？")) {
                            setPreviewVisible(false);
                            setHasChanges(false);
                        }
                    } else {
                        setPreviewVisible(false);
                    }
                }}
                width="90%"
                extra={
                    <div style={{ display: "flex", gap: "8px" }}>
                        {hasChanges && (
                            <Button
                                type="default"
                                icon={<SaveOutlined />}
                                loading={saving}
                                onClick={handleSaveChanges}
                            >
                                保存修改
                            </Button>
                        )}
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => previewData && window.open(previewData.excel_signed_url, "_blank")}
                        >
                            下载 Excel
                        </Button>
                    </div>
                }
            >
                {previewLoading ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: "16px" }}>加载中...</p>
                    </div>
                ) : (
                    previewData && (
                        <div>
                            {/* 航班信息（可编辑） */}
                            <div style={{ 
                                marginBottom: "16px", 
                                padding: "12px", 
                                backgroundColor: "#f5f5f5", 
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "16px"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <strong>航班号：</strong>
                                    <Input
                                        value={editedFlightNumber}
                                        onChange={(e) => {
                                            setEditedFlightNumber(e.target.value);
                                            setHasChanges(true);
                                        }}
                                        placeholder="未识别"
                                        style={{ width: "150px" }}
                                    />
                                </div>
                                <Divider type="vertical" />
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <strong>日期：</strong>
                                    <DatePicker
                                        value={editedFlightDate}
                                        onChange={(date) => {
                                            setEditedFlightDate(date);
                                            setHasChanges(true);
                                        }}
                                        placeholder="未识别"
                                        format="YYYY-MM-DD"
                                        style={{ width: "150px" }}
                                    />
                                </div>
                            </div>

                            {/* 左右布局 */}
                            <Row gutter={16}>
                                {/* 左侧：原文件预览 */}
                                <Col span={12}>
                                    <div style={{ 
                                        backgroundColor: "#f5f5f5", 
                                        padding: "16px", 
                                        borderRadius: "4px",
                                        height: "calc(100vh - 200px)",
                                        overflow: "auto"
                                    }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                            <h3 style={{ margin: 0 }}>原文件预览</h3>
                                            {previewData.pdf_signed_urls && previewData.pdf_signed_urls.length > 1 && (
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <Button
                                                        size="small"
                                                        icon={<LeftOutlined />}
                                                        disabled={currentFileIndex === 0}
                                                        onClick={() => setCurrentFileIndex(currentFileIndex - 1)}
                                                    />
                                                    <span style={{ fontSize: "14px", color: "#666" }}>
                                                        {currentFileIndex + 1} / {previewData.pdf_signed_urls.length}
                                                    </span>
                                                    <Button
                                                        size="small"
                                                        icon={<RightOutlined />}
                                                        disabled={currentFileIndex === previewData.pdf_signed_urls.length - 1}
                                                        onClick={() => setCurrentFileIndex(currentFileIndex + 1)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {(() => {
                                            // 处理单个或多个文件
                                            const fileUrls = previewData.pdf_signed_urls || (previewData.pdf_signed_url ? [previewData.pdf_signed_url] : []);
                                            const currentUrl = fileUrls[currentFileIndex];
                                            
                                            if (!currentUrl) {
                                                return <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>暂无文件</div>;
                                            }
                                            
                                            const isImage = currentUrl.toLowerCase().match(/\.(jpg|jpeg|png|bmp|webp|tiff|tif)/);
                                            
                                            return isImage ? (
                                                // 图片预览
                                                <img 
                                                    src={currentUrl} 
                                                    alt={`原文件 ${currentFileIndex + 1}`}
                                                    style={{ 
                                                        width: "100%", 
                                                        height: "auto",
                                                        border: "1px solid #d9d9d9",
                                                        borderRadius: "4px"
                                                    }} 
                                                />
                                            ) : (
                                                // PDF 预览
                                                <iframe
                                                    src={currentUrl}
                                                    style={{
                                                        width: "100%",
                                                        height: "calc(100vh - 280px)",
                                                        border: "1px solid #d9d9d9",
                                                        borderRadius: "4px"
                                                    }}
                                                    title={`原文件 ${currentFileIndex + 1}`}
                                                />
                                            );
                                        })()}
                                    </div>
                                </Col>

                                {/* 右侧：识别结果 */}
                                <Col span={12}>
                                    <div style={{ 
                                        backgroundColor: "#fff", 
                                        padding: "16px", 
                                        borderRadius: "4px",
                                        border: "1px solid #f0f0f0"
                                    }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                            <h3 style={{ margin: 0 }}>识别结果（可编辑）</h3>
                                            {hasChanges && (
                                                <Tag color="orange">未保存</Tag>
                                            )}
                                        </div>
                                        <Table
                                            columns={previewColumns}
                                            dataSource={editedData}
                                            rowKey={(record, index) => index}
                                            pagination={false}
                                            scroll={{ x: 1100, y: "calc(100vh - 350px)" }}
                                            size="small"
                                            bordered
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )
                )}
            </Drawer>
        </div>
    );
};

export default UldOcrPage;
